import { DateTime } from 'luxon'
import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import Button from '../../../../../components/atoms/button/Button'
import Counter from '../../../../../components/atoms/counter/Counter'
import MonthPicker from '../../../../../components/atoms/monthPicker/MonthPicker'
import Switch from '../../../../../components/atoms/switch/Switch'
import Text from '../../../../../components/atoms/text/Text'
import DatePicker from '../../../../../components/molecules/datePicker/DatePicker'
import ListItem from '../../../../../components/molecules/listItem/ListItem'
import Screen from '../../../../../components/molecules/screen/Screen'
import ToggleButton from '../../../../../components/molecules/toggleButton/ToggleButton'
import routes from '../../../../../config/routes'
import { useHolidays, useSeatById, useSeatsQueryParams } from '../../../../../modules/seats/hooks'
import { useBookAssistant } from '../../../../../providers/bookAssistant'
import { IShift, IShifts, SHIFTS } from '../../../../../providers/bookAssistant/shifts'
import { ITypes, TYPES } from '../../../../../providers/bookAssistant/types'
import { useCardTransition } from '../../../../../providers/cardTransition/cardTransition'
import { VARIANTS } from '../../../../../types/Components.types'
import { isSameDay, nextWorkingDay } from '../../../../../utils'
import styles from './BookSeatScreen.module.css'
import ReservationTypeSelector, {
  RESERVATION_TYPE_OPTIONS,
} from './components/ReservationTypeSelector/ReservationTypeSelector'

const BookSeatScreen = () => {
  const navigate = useNavigate()
  const { type, months, seat: seatId, day, shift, setType, setDay, setNumberOfMonths, setShift } = useBookAssistant()
  const queryParams = useSeatsQueryParams()
  const { data: seat } = useSeatById({ seatId, queryParams })
  const { data: holidays } = useHolidays()

  const { openCard, closeCard } = useCardTransition()

  const maxDate = useMemo(() => {
    if (type === TYPES.MONTH) {
      return DateTime.local().plus({ months: 1 }).startOf('month')
    }
    return DateTime.local().plus({ months: 2 }).endOf('month').startOf('day')
  }, [type])

  const handleContinueClick = useCallback(() => {
    navigate(routes.book.new.seat.confirmation)
  }, [navigate])

  const handleSelectReservationType = useCallback(
    (selectedType: ITypes[keyof ITypes]) => {
      if (selectedType === TYPES.MONTH) {
        const today = DateTime.local()
        let targetDay = today.plus({ months: 1 }).startOf('month')
        if (holidays?.some((holiday) => isSameDay(DateTime.fromJSDate(holiday), targetDay)) || targetDay.isWeekend) {
          targetDay = nextWorkingDay(targetDay) as DateTime<true>
        }
        setType({ type: TYPES.MONTH })
        setDay({ day: targetDay.toJSDate() })
      } else {
        const today = DateTime.local()
        let target = today.plus({ days: 1 })

        if (target.weekday === 6 || target.weekday === 7) {
          target = target.plus({ days: 8 - target.weekday })
        }

        if (holidays?.some((holiday) => isSameDay(DateTime.fromJSDate(holiday), target))) {
          target = target.plus({ days: 1 })
        }

        setType({ type: selectedType })
        setDay({ day: target.toJSDate() })
      }
      closeCard?.()
    },
    [holidays, setType, setDay, closeCard],
  )

  const handleReservationTypeClick = useCallback(() => {
    openCard?.({
      title: 'Tipo de reserva',
      Component: <ReservationTypeSelector selectedType={type} onSelect={handleSelectReservationType} />,
    })
  }, [openCard, type, handleSelectReservationType])

  const handleMonthsChange = useCallback(
    (months: number) => {
      setNumberOfMonths?.({ months })
    },
    [setNumberOfMonths],
  )

  const handleBookUndefinedChange = useCallback(
    (checked: boolean) => {
      handleMonthsChange(checked ? -1 : 1)
    },
    [handleMonthsChange],
  )

  const handleChangeSeatClick = useCallback(() => {
    navigate(routes.book.new.seat.place)
  }, [navigate])

  const handleDateSelectorClick = useCallback(() => {
    openCard?.({
      title: type === TYPES.MONTH ? 'Elige el mes' : 'Elige el día',
      Component:
        type === TYPES.MONTH ? (
          <MonthPicker
            initialDate={day}
            minDate="today"
            maxDate={maxDate}
            onMonthConfirm={(month) => {
              const day = DateTime.fromJSDate(new Date()).set({ month, day: 1 })
              setDay({ day: day.toJSDate() })
            }}
          />
        ) : (
          <DatePicker
            disabledDays={[0, 6]}
            disabledDates={holidays}
            initialDate={day}
            onDateConfirm={(date: Date | Date[]) => {
              setDay({ day: date as Date })
            }}
            minDate="today"
            maxDate={maxDate}
          />
        ),
    })
  }, [openCard, type, day, holidays, maxDate, setDay])

  const isBookOfTypeMonth = type === TYPES.MONTH
  const isBookUndefined = isBookOfTypeMonth && months === -1

  return (
    <Screen
      headerProps={{
        backEnabled: true,
        variant: VARIANTS.flamingo,
        title: 'Nueva reserva',
      }}
    >
      <div className={styles.container}>
        <div className={styles.content}>
          <ListItem label="Tipo de reserva">
            <Button
              className={styles.button}
              autoSize
              variant={VARIANTS.secondary}
              inverted
              onClick={handleReservationTypeClick}
            >
              <Text size="sm" variant={VARIANTS.secondary} weight="500">
                {RESERVATION_TYPE_OPTIONS.find((option) => option.value === type)?.label || 'Seleccionar'}
              </Text>
            </Button>
          </ListItem>

          {isBookOfTypeMonth && (
            <div className={styles.coworkingAccessWrapper}>
              <Text>¿Cuando quieres acceder al coworking?</Text>
              <div className={styles.shitsOptions}>
                {(Object.keys(SHIFTS) as Array<keyof IShifts>).map((key) => {
                  const { from, to, label, suitableTo } = SHIFTS[key] as IShift
                  const isActive = shift && shift.from === from && shift.to === to
                  const isSuitable = suitableTo.some((bookType) => bookType === type)
                  if (!isSuitable) {
                    return null
                  }

                  return (
                    <ToggleButton
                      key={key}
                      className={styles.shiftButton}
                      isActive={isActive}
                      onClick={() => setShift({ shift: SHIFTS[key] })}
                    >
                      <Text size="sm" variant={isActive ? VARIANTS.white : VARIANTS.secondary} weight="500">
                        {label}
                      </Text>
                    </ToggleButton>
                  )
                })}
              </div>
            </div>
          )}

          {isBookOfTypeMonth && (
            <>
              <ListItem label="De manera indefinida">
                <Switch name="undefined" checked={isBookUndefined} onChange={handleBookUndefinedChange} />
              </ListItem>

              {!isBookUndefined && (
                <ListItem label="¿Cuántos meses?">
                  <Counter min={1} max={12} onChange={handleMonthsChange} value={months} />
                </ListItem>
              )}

              <Text size="s" className={styles.helpText}>
                Se te aplicarán los descuentos automáticamente. 5% dto. el sexto mes y si renuevas durante 12 meses, el
                último es gratis.
              </Text>
            </>
          )}

          <ListItem label="Cuándo quieres reservar">
            <Button
              className={styles.button}
              autoSize
              variant={VARIANTS.secondary}
              inverted
              onClick={handleDateSelectorClick}
            >
              {!isBookOfTypeMonth ? (
                <Text size="sm" variant={VARIANTS.secondary} weight="500">
                  {day ? DateTime.fromJSDate(day).toLocaleString(DateTime.DATE_MED) : 'Seleccionar'}
                </Text>
              ) : (
                <Text className={styles.month} size="sm" variant={VARIANTS.secondary} weight="500">
                  {day ? DateTime.fromJSDate(day).toLocaleString({ month: 'long' }) : 'Seleccionar'}
                </Text>
              )}
            </Button>
          </ListItem>

          {isBookOfTypeMonth ? (
            <Text size="s" className={styles.helpText}>
              {`Al reservar un mes, la reserva se realiza para el mes natural. Es decir, si reservas hoy, ${DateTime.local().toLocaleString(
                DateTime.DATE_FULL,
              )}, tendrá validez hasta ${DateTime.local().endOf('month').toLocaleString(DateTime.DATE_FULL)}`}
            </Text>
          ) : null}

          <ListItem label="Tu sitio">
            <Button
              className={styles.button}
              autoSize
              variant={VARIANTS.secondary}
              inverted
              onClick={handleChangeSeatClick}
              disabled={!day || !shift}
            >
              <Text size="sm" variant={VARIANTS.secondary} weight="500">
                {seat ? seat.name : 'Elígelo'}
              </Text>
            </Button>
          </ListItem>
        </div>

        <div className={styles.actions}>
          <Button
            variant={VARIANTS.primary}
            className={styles.action}
            onClick={handleContinueClick}
            disabled={!day || !shift || !seat}
          >
            Continuar
          </Button>
        </div>
      </div>
    </Screen>
  )
}

export default BookSeatScreen
